<template>
  <component
    :is="componentElement"
    :data-id="elementId"
    class="lottie-animation-container"
    :style="getCurrentStyle"
    v-bind="$attrs"
    @mouseenter="hoverStarted"
    @mouseleave="hoverEnded"
  >
    <span class="hidden"></span>
  </component>
</template>

<script>
import Lottie from 'lottie-web'

export default {
  name: 'Lottie',

  props: {
    animationData: {
      type: Object,
      default: () => ({})
    },
    animationLink: {
      type: String,
      default: ''
    },
    loop: {
      type: [Boolean, Number],
      default: true
    },
    autoPlay: {
      type: Boolean,
      default: true
    },
    width: {
      type: [Number, String],
      default: '100%'
    },
    height: {
      type: [Number, String],
      default: '100%'
    },
    speed: {
      type: Number,
      default: 1
    },
    delay: {
      type: Number,
      default: 0
    },
    direction: {
      type: String,
      default: 'forward'
    },
    pauseOnHover: {
      type: Boolean,
      default: false
    },
    playOnHover: {
      type: Boolean,
      default: false
    },
    backgroundColor: {
      type: String,
      default: 'transparent'
    },
    pauseAnimation: {
      type: Boolean,
      default: false
    },
    renderer: {
      type: String,
      default: 'svg',
      validator: (value) => {
        return ['svg', 'canvas', 'html'].includes(value)
      }
    },
    rendererSettings: {
      type: Object,
      default: () => ({})
    },
    assetsPath: {
      type: String,
      default: ''
    },
    componentElement: {
      type: String,
      default: 'div'
    }
  },

  data () {
    return {
      lottieAnimation: null,
      elementId: '',
      // Available options: 1, -1
      invertDirection: 1
    }
  },

  watch: {
    // watch for changes in props.pauseAnimation
    pauseAnimation (newValue) {
      // error if pauseAnimation is true and pauseOnHover is also true or playOnHover is also true
      if ((this.pauseOnHover || this.playOnHover) && newValue) {
        console.error(
          'If you are using pauseAnimation prop, please remove the props pauseOnHover and playOnHover'
        )
        return
      }

      // control the animation play state
      if (this.lottieAnimation) {
        if (newValue) {
          this.lottieAnimation.pause()
        } else {
          this.lottieAnimation.play()
        }
      }
    }
  },

  computed: {
    getCurrentStyle () {
      let width = this.width
      let height = this.height

      // set to px values if a number is passed
      if (typeof this.width === 'number') {
        width = `${this.width}px`
      }

      if (typeof this.height === 'number') {
        height = `${this.height}px`
      }

      return {
        '--lottie-animation-container-width': width,
        '--lottie-animation-container-height': height,
        '--lottie-animation-container-background-color': this.backgroundColor
      }
    }
  },

  methods: {
    async loadLottie (element) {
      let autoPlay = this.autoPlay

      if (this.playOnHover) {
        autoPlay = false
      }

      // creating a copy of the animation data to prevent the original data from being modified
      // also needed to render multiple animations on the same page
      let animationData = {}
      if (Object.keys(this.animationData).length > 0) {
        animationData = JSON.parse(JSON.stringify(this.animationData))
      }

      if (this.animationLink !== '') {
        try {
          const response = await fetch(this.animationLink)
          animationData = await response.json()
        } catch (error) {
          return
        }
      }

      let loop = this.loop

      // drop the loop by one
      if (typeof loop === 'number') {
        if (loop > 0) {
          loop = loop - 1
        }
      }

      if (this.delay > 0) {
        autoPlay = false
      }

      const lottieAnimationConfig = {
        container: element,
        renderer: this.renderer,
        loop:
        loop,
        autoplay: autoPlay,
        animationData,
        assetsPath: this.assetsPath
      }

      if (Object.keys(this.rendererSettings).length > 0) {
        lottieAnimationConfig.rendererSettings = this.rendererSettings
      }

      // actually load the animation
      this.lottieAnimation = Lottie.loadAnimation(lottieAnimationConfig)

      setTimeout(() => {
        autoPlay = this.autoPlay

        if (this.playOnHover) {
          this.lottieAnimation.pause()
        } else if (autoPlay) {
          this.lottieAnimation.play()
        } else {
          this.lottieAnimation.pause()
        }

        /**
         * Emit an `onAnimationLoaded` event when the animation is loaded
         * This should help with times when you want to run functions on the ref of the element
         */
        this.$emit('onAnimationLoaded')
      }, this.delay)

      this.lottieAnimation.setSpeed(this.speed)

      if (this.direction === 'reverse') {
        this.lottieAnimation.setDirection(-1)
      }
      if (this.direction === 'normal') {
        this.lottieAnimation.setDirection(1)
      }

      if (this.pauseAnimation) {
        this.lottieAnimation.pause()
      } else if (this.playOnHover) {
        this.lottieAnimation.pause()
      }

      // set the emit events
      this.lottieAnimation.addEventListener('loopComplete', () => {
        if (this.direction === 'alternate') {
          this.lottieAnimation.stop()
          this.invertDirection = this.invertDirection * -1 // invert direction
          this.lottieAnimation.setDirection(this.invertDirection)
          this.lottieAnimation.play()
        }
        this.$emit('onLoopComplete')
      })

      this.lottieAnimation.addEventListener('complete', () => {
        this.$emit('onComplete')
      })

      this.lottieAnimation.addEventListener('enterFrame', () => {
        this.$emit('onEnterFrame')
      })

      this.lottieAnimation.addEventListener('segmentStart', () => {
        this.$emit('onSegmentStart')
      })
    },

    // function to check if the container is being hovered
    hoverStarted () {
      if (this.lottieAnimation && this.pauseOnHover) {
        this.lottieAnimation.pause()
      }

      if (this.lottieAnimation && this.playOnHover) {
        this.lottieAnimation.play()
      }
    },

    // function to check if the container is no longer being hovered
    hoverEnded () {
      if (this.lottieAnimation && this.pauseOnHover) {
        this.lottieAnimation.play()
      }
      if (this.lottieAnimation && this.playOnHover) {
        this.lottieAnimation.pause()
      }
    },

    // method to play the animation
    play () {
      if (this.lottieAnimation) {
        this.lottieAnimation.play()
      }
    },

    // method to pause the animation
    pause () {
      if (this.lottieAnimation) {
        this.lottieAnimation.pause()
      }
    },

    // method to stop the animation. It will reset the animation to the first frame
    stop () {
      if (this.lottieAnimation) {
        this.lottieAnimation.stop()
      }
    },

    destroy () {
      if (this.lottieAnimation) {
        this.lottieAnimation.destroy()
      }
    },

    setSpeed (speed = 1) {
      // speed: 1 is normal speed.
      if (speed <= 0) {
        throw new Error('Speed must be greater than 0')
      }

      if (this.lottieAnimation) {
        this.lottieAnimation.setSpeed(speed)
      }
    },

    setDirection (direction) {
      if (this.lottieAnimation) {
        if (direction === 'forward') {
          this.lottieAnimation.setDirection(1)
        } else if (direction === 'reverse') {
          this.lottieAnimation.setDirection(-1)
        }
      }
    },

    goToAndStop (frame, isFrame = true) {
      // value: numeric value.
      // isFrame: defines if first argument is a time based value or a frame based (default true).
      if (this.lottieAnimation) {
        this.lottieAnimation.goToAndStop(frame, isFrame)
      }
    },

    goToAndPlay (frame, isFrame = true) {
      // value: numeric value
      // isFrame: defines if first argument is a time based value or a frame based (default true).
      if (this.lottieAnimation) {
        this.lottieAnimation.goToAndPlay(frame, isFrame)
      }
    },

    playSegments (segments, forceFlag = false) {
      // segments: array. Can contain 2 numeric values that will be used as first and last frame of the animation. Or can contain a sequence of arrays each with 2 numeric values.
      // forceFlag: boolean. If set to false, it will wait until the current segment is complete. If true, it will update values immediately.
      if (this.lottieAnimation) {
        this.lottieAnimation.playSegments(segments, forceFlag)
      }
    },

    setSubFrame (useSubFrame = true) {
      // useSubFrames: If false, it will respect the original AE fps. If true, it will update on every requestAnimationFrame with intermediate values. Default is true.
      if (this.lottieAnimation) {
        this.lottieAnimation.setSubframe(useSubFrame)
      }
    },

    getDuration (inFrames = true) {
      if (this.lottieAnimation) {
        return this.lottieAnimation.getDuration(inFrames)
      }
    },

    updateDocumentData (documentData, index = 0) {
      if (this.lottieAnimation) {
        this.lottieAnimation.renderer.elements[index].updateDocumentData(documentData)
      }
    },

    // function to generate random strings for IDs
    makeId (length) {
      let result = ''
      const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
      const charactersLength = characters.length
      for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength))
      }
      return result
    },

    setupLottie (elementID) {
      if (this.pauseOnHover && this.playOnHover) {
        throw new Error('You cannot set pauseOnHover and playOnHover for Vue2-Lottie at the same time.')
      }

      if (this.animationLink === '' && Object.keys(this.animationData).length === 0) {
        throw new Error('You must provide either animationLink or animationData')
      }

      // Unfortunately, this is a hack-fix for ssr. We need to wait for the element to be rendered before we can load the animation.
      // One day I will figure out how to do this properly.
      const interval = setInterval(() => {
        if (this.checkIfContainerExists(elementID)) {
          clearInterval(interval)
          const element = document.querySelector(`[data-id="${elementID}"]`)

          if (element) {
            this.loadLottie(element) // load the animation
          }
        }
      }, 0)
    },

    checkIfContainerExists (elementID) {
      return document.querySelector(`[data-id="${elementID}"]`) !== null
    }
  },

  mounted () {
    this.elementId = this.makeId(20) // generate a random id for the container
    this.setupLottie(this.elementId)
  }
}
</script>

<style scoped>
.lottie-animation-container {
  width: var(--lottie-animation-container-width);
  height: var(--lottie-animation-container-height);
  background-color: var(--lottie-animation-container-background-color);
  overflow: hidden;
  margin: 0 auto;
}
</style>
